import { useState } from 'react';
import { Table, TableBody } from '@/components/ui';
import { DataTablePagination, PanelInfo } from '@/components/custom';
import { ShieldAlert } from 'lucide-react';
import { DataTableHeader } from './DataTableHeader';
import { DataTableDesktopRow } from './components/DataTableDesktopRow';
import { DataTableMobileCard } from './components/DataTableMobileCard';
import { SnapshotModal } from './components/SnapshotModal';
import AuditFilters from './AuditFilters';
import useAuditLogs from './hooks/useAuditLogs';
import type { IAuditLog } from '@/interfaces';

const DataTable = () => {
	const { data, isLoading, auditLogs } = useAuditLogs();
	const [selectedLog, setSelectedLog] = useState<IAuditLog | null>(null);

	const handleViewSnapshot = (log: IAuditLog) => setSelectedLog(log);

	return (
		<div className="space-y-4">
			<AuditFilters isLoading={isLoading} />

			{isLoading && (
				<div className="space-y-2">
					{Array.from({ length: 5 }).map((_, i) => (
						<div key={i} className="h-12 rounded-md bg-muted animate-pulse" />
					))}
				</div>
			)}

			{!isLoading && auditLogs.length === 0 && (
				<PanelInfo
					title="Aucun log trouvé"
					description="Aucune action enregistrée pour ces critères."
					className="py-10 border-none"
					icon={ShieldAlert}
				/>
			)}

			{!isLoading && auditLogs.length > 0 && (
				<>
					<div className="hidden md:block border rounded-md">
						<Table>
							<DataTableHeader />
							<TableBody>
								{auditLogs.map(log => (
									<DataTableDesktopRow
										key={log._id}
										log={log}
										onViewSnapshot={handleViewSnapshot}
									/>
								))}
							</TableBody>
						</Table>
					</div>

					<div className="block md:hidden space-y-3">
						{auditLogs.map(log => (
							<DataTableMobileCard
								key={log._id}
								log={log}
								onViewSnapshot={handleViewSnapshot}
							/>
						))}
					</div>

					{data?.pagination && (
						<DataTablePagination pageCount={data.pagination.pageCount} />
					)}
				</>
			)}

			<SnapshotModal
				isOpen={!!selectedLog}
				onClose={() => setSelectedLog(null)}
				snapshot={selectedLog?.snapshot}
				entityId={selectedLog?.entityId ?? ''}
			/>
		</div>
	);
};

export default DataTable;
